import React from 'react';
import { View } from '@react-pdf/renderer';
import { generate } from 'shortid';
import FlexTableHeadingRow from '../FlexTableHeadingRow';
import FlexTableRow from '../FlexTableRow';
import styles from '../../styles';
import { TableState } from '../../utils';
import { FlexTableCellItem } from '../../types';

type HeadingRowsProps = {
  tableState: TableState;
  headingRows: Array<FlexTableCellItem[]>;
  rowIndex: number;
  headingStyles?: Record<string, any>;
  cellStyles?: Record<string, any>;
  rowBordered?: boolean;
  cellBordered?: boolean;
  headerContinuedText?: string;
};

const MAIN_HEADING_HEIGHT = 24;

const HeadingRows: React.FC<HeadingRowsProps> = ({
  tableState,
  headingRows,
  rowIndex,
  headingStyles = {},
  cellStyles = {},
  rowBordered = true,
  cellBordered = false,
  headerContinuedText = 'Continued'
}) => {
  /**
   * Determines if the HeadingRows should be displayed based on row position and page.
   * Rule: Only the first HeadingRows on each page should be visible.
   *
   * The heading should show if:
   * 1. This is the very first body row (rowIndex === 2, accounting for header rows)
   * 2. OR the previous row is on a different page (page break occurred)
   *
   * @param currentPageNumber - The actual page number from the render prop
   * @returns true if heading should be displayed, false otherwise
   */
  const shouldDisplayHeadingRows = (currentPageNumber: number): boolean => {
    const { rowToPageMap } = tableState;

    // Always show heading for the first body row
    if (rowIndex === 2) {
      return true;
    }

    const previousRowIndex = rowIndex - 1;
    const previousPage = rowToPageMap[previousRowIndex];

    // If previous row page info is not available yet, don't show heading
    if (previousPage === undefined) {
      return false;
    }

    // Show heading only if we're on a new page (page break occurred)
    // Use the actual currentPageNumber from render prop instead of rowToPageMap
    return currentPageNumber > previousPage;
  };

  const minHeight =
    headingStyles.height &&
    typeof headingStyles.height === 'number' &&
    rowIndex === 2
      ? headingStyles.height + MAIN_HEADING_HEIGHT
      : 'auto';

  return (
    <View
      style={{ ...styles.tableContainer, minHeight }}
      render={({ pageNumber }) => {
        if (!shouldDisplayHeadingRows(pageNumber)) {
          return null;
        }

        return (
          <View>
            {headingRows.map(
              (headingRow: FlexTableCellItem[], headingRowIndex: number) => {
                if (headingRowIndex === 0) {
                  return (
                    <FlexTableHeadingRow
                      row={headingRow}
                      tableState={tableState}
                      pageNumber={pageNumber}
                      key={`table-row-${generate()}`}
                      continuedText={headerContinuedText}
                    />
                  );
                }
                return (
                  <FlexTableRow
                    key={`table-row-${generate()}`}
                    row={headingRow}
                    rowIndex={headingRowIndex}
                    cellStyles={cellStyles}
                    headingStyles={headingStyles}
                    rowBordered={rowBordered}
                    tableState={tableState}
                    cellBordered={cellBordered}
                  />
                );
              }
            )}
          </View>
        );
      }}
    />
  );
};

export default HeadingRows;
